// PDF scaling and fit-to-page utilities for PdfTableViewer

/**
 * Find the PDF content container by traversing up the DOM
 * @param {HTMLElement} canvas - Canvas element
 * @returns {HTMLElement|null} - Container element or null
 */
export const findPdfContainer = (canvas) => {
  // Get the PDF content container - look for the CardContent element
  let container = canvas.parentElement;
  while (
    container &&
    !container.getAttribute("class")?.includes("MuiCardContent")
  ) {
    container = container.parentElement;
  }

  if (!container) {
    // Fallback to direct parent
    container = canvas.parentElement;
  }

  return container;
};

/**
 * Calculate available space for PDF in container
 * @param {HTMLElement} container - Container element
 * @returns {Object} - Available width and height
 */
export const calculateAvailableSpace = (container) => {
  const containerRect = container.getBoundingClientRect();

  return {
    availableWidth: containerRect.width - 4, // Very minimal margins for aggressive fit
    availableHeight: containerRect.height - 16, // Reduced margins for nav bar
  };
};

/**
 * Calculate optimal scale to fit PDF in available space
 * @param {Object} pdfDimensions - PDF width and height
 * @param {Object} availableSpace - Available width and height
 * @returns {number} - Optimal scale factor
 */
export const calculateOptimalScale = (pdfDimensions, availableSpace) => {
  const { width: pdfWidth, height: pdfHeight } = pdfDimensions;
  const { availableWidth, availableHeight } = availableSpace;

  // Calculate what scale would be needed to fit the PDF optimally
  const scaleX = availableWidth / pdfWidth;
  const scaleY = availableHeight / pdfHeight;

  // Use the smaller scale to ensure the page fits completely
  const optimalScale = Math.min(scaleX, scaleY);

  // Clamp the scale within reasonable bounds
  return Math.max(0.2, Math.min(2.5, optimalScale));
};

/**
 * Calculate fit-to-page scale for PDF
 * @param {HTMLCanvasElement} canvas - PDF canvas element
 * @param {Object} pdfPageDimensions - PDF page dimensions
 * @returns {number|null} - Calculated scale or null if calculation fails
 */
export const calculateFitToPageScale = (canvas, pdfPageDimensions) => {
  if (!canvas || !pdfPageDimensions) return null;

  const container = findPdfContainer(canvas);
  if (!container) return null;

  const availableSpace = calculateAvailableSpace(container);
  const optimalScale = calculateOptimalScale(pdfPageDimensions, availableSpace);

  return optimalScale;
};

/**
 * Calculate maximum width for navigation bar based on PDF container
 * @param {HTMLCanvasElement} canvas - PDF canvas element
 * @returns {string} - CSS width value for navigation bar
 */
export const calculateNavBarMaxWidth = (canvas) => {
  if (!canvas) {
    return "calc(100vw - 32px)"; // Fallback to viewport calculation
  }

  const container = findPdfContainer(canvas);
  if (!container) {
    return "calc(100vw - 32px)"; // Fallback if container not found
  }

  const { availableWidth } = calculateAvailableSpace(container);
  // Use slightly less than available width to account for padding/margins
  return `${Math.max(300, availableWidth - 24)}px`; // Minimum 300px width
};
